import React, { useEffect, useState } from "react";
import {
  Download,
  Phone,
  CheckCircle,
  XCircle,
  Users,
  Loader2,
  BarChart2,
  Eye,
} from "lucide-react";
import {
  GetCallLogReport,
  GetCallLogReportExcel,
} from "../Services/ReportService";
import Cookies from "js-cookie";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import { formatDateTimeU } from "../../../../Utils/FormUtils";

const CallLog = () => {
  const user = JSON.parse(Cookies.get("user") || "{}");
  const userName = user?.name || "Employee";

  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [alert, setAlert] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({
    totalCalls: 0,
    connected: 0,
    notConnected: 0,
    connectionPercentage: 0,
    candidates: 0,
  });

  useEffect(() => {
    loadReport(false);
  }, []);

  const loadReport = async (showButtonLoader = false) => {
    if (new Date(fromDate) > new Date(toDate)) {
      setAlert({
        type: "warning",
        message: "From Date cannot be greater than To Date.",
      });
      return;
    }

    try {
      if (showButtonLoader) setViewLoading(true);

      const payload = { fromDate, toDate, exportExcel: false };
      const response = await GetCallLogReport(payload);
      const data = response?.data || [];

      setReportData(data);

      const connected = data.filter(
        (x) => x.callResponse?.toUpperCase() === "CONNECTED",
      ).length;
      const totalCalls = data.length;
      const notConnected = totalCalls - connected;
      const uniqueCandidates = new Set(data.map((x) => x.leadID)).size;

      setSummary({
        totalCalls,
        connected,
        notConnected,
        candidates: uniqueCandidates,
        connectionPercentage:
          totalCalls > 0 ? ((connected * 100) / totalCalls).toFixed(2) : 0,
      });
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Failed to load report.",
      });
    } finally {
      if (showButtonLoader) setViewLoading(false);
      setPageLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      setExportLoading(true);
      const payload = { fromDate, toDate, exportExcel: true };
      const response = await GetCallLogReportExcel(payload);

      const user = JSON.parse(Cookies.get("user") || "{}");
      const userName = user?.name || "Employee";

      const fileName =
        fromDate === toDate
          ? `${userName}_CallLog_${fromDate}.xlsx`
          : `${userName}_CallLog_${fromDate}_to_${toDate}.xlsx`;

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportLoading(false);
    } catch (error) {
      console.log(error);
      setExportLoading(false);

      if (error instanceof Blob) {
        const text = await error.text();
        const json = JSON.parse(text);
        setAlert({
          type: "error",
          message: json?.message || "Something went wrong.",
        });
        return;
      }

      setAlert({ type: "error", message: "Something went wrong." });
    }
  };

  return (
    <>
      <div className="p-6 pt-4 min-h-screen bg-gray-50">
        {/* FILTERS */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
              <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                From date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
              <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                To date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            <div className="flex gap-2 flex-1 min-w-[140px]">
              <button
                onClick={() => loadReport(true)}
                disabled={viewLoading}
                className="flex-1 flex items-center justify-center gap-2 bg-primary text-white rounded-lg py-2 px-4 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60 whitespace-nowrap"
              >
                {viewLoading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <>
                    <Eye size={15} />
                    View logs
                  </>
                )}
              </button>

              <button
                onClick={handleDownloadReport}
                disabled={exportLoading}
                className="flex-1 flex items-center justify-center gap-2 border border-gray-200 text-gray-600 rounded-lg py-2 px-4 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {exportLoading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={15} />
                    Export
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-5">
          <StatCard
            title="Total Calls"
            value={summary.totalCalls}
            icon={<Phone size={16} />}
            iconClass="bg-blue-50 text-blue-500"
          />
          <StatCard
            title="Connected"
            value={summary.connected}
            icon={<CheckCircle size={16} />}
            iconClass="bg-green-50 text-green-600"
          />
          <StatCard
            title="Not Connected"
            value={summary.notConnected}
            icon={<XCircle size={16} />}
            iconClass="bg-red-50 text-red-500"
          />
          <StatCard
            title="Connection %"
            value={`${summary.connectionPercentage}%`}
            icon={<BarChart2 size={16} />}
            iconClass="bg-amber-50 text-amber-500"
          />
          <StatCard
            title="Candidates"
            value={summary.candidates}
            icon={<Users size={16} />}
            iconClass="bg-purple-50 text-purple-500"
          />
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">
              Daily call log
            </h2>
            {!pageLoading && (
              <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-3 py-1">
                {reportData.length}{" "}
                {reportData.length === 1 ? "entry" : "entries"}
              </span>
            )}
          </div>

          <div className="h-[500px] overflow-y-auto custom-scrollbar">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-100 border-b border-gray-200">
                <tr>
                  {[
                    "Date & time",
                    "Candidate",
                    "Phone",
                    "Company",
                    "Role",
                    "Response",
                    "Remark",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-5 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {pageLoading ? (
                  <tr>
                    <td colSpan="7" className="py-20 text-center">
                      <Loader2
                        size={24}
                        className="animate-spin mx-auto text-primary"
                      />
                    </td>
                  </tr>
                ) : reportData.length > 0 ? (
                  reportData.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-5 py-3.5 text-gray-500 text-xs whitespace-nowrap">
                        {formatDateTimeU(item.actionDate)}
                      </td>

                      <td className="px-5 py-3.5 font-semibold text-gray-800">
                        {item.candidateName}
                      </td>

                      <td className="px-5 py-3.5 text-gray-500 text-xs">
                        {item.phoneNo}
                      </td>

                      <td className="px-5 py-3.5 text-gray-700">
                        {item.targetOrganization}
                      </td>

                      <td className="px-5 py-3.5 text-gray-700">
                        {item.targetRole}
                      </td>

                      <td className="px-5 py-3.5">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                            item.callResponse?.toUpperCase() === "CONNECTED"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-600"
                          }`}
                        >
                          {item.callResponse?.toUpperCase() === "CONNECTED" ? (
                            <CheckCircle size={10} />
                          ) : (
                            <XCircle size={10} />
                          )}
                          {item.callResponse}
                        </span>
                      </td>

                      <td className="px-5 py-3.5 text-gray-500 text-xs max-w-[160px] truncate">
                        {item.remark || "—"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-16">
                      <p className="text-gray-500 text-sm">
                        No call logs found for this date range..
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
          duration={alert?.duration || 1200}
        />
      )}
    </>
  );
};

const StatCard = ({ title, value, icon, iconClass }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-between gap-3">
    <div>
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
        {title}
      </p>
      <h2 className="text-xl font-semibold text-gray-800 leading-none">
        {value}
      </h2>
    </div>
    <div
      className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconClass}`}
    >
      {icon}
    </div>
  </div>
);

export default CallLog;
